import {createStyles} from '@mantine/core';
import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {FieldDefinitionType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ButtonSize} from 'ui-library/buttons/Button';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import Menu, {ROLES} from 'ui-library/menus/Menu';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import MenuOptionWithFloatingSubmenu from 'ui-library/menus/MenuOptionWithFloatingSubmenu';
import {defaultRenderOptionContent, getOptionRefForIndex} from 'ui-library/menus/MenuUtil';
import Option from 'ui-library/menus/Option';
import OptionContainer from 'ui-library/menus/OptionContainer';

import {
  LIBRARY_PARAMETER_DEFINITION_DATA_TYPES,
  LibraryParameterDefinitionDataType,
} from '../ParameterLibraryTypes';
import {LIBRARY_PARAMETER_DEFINITION_DATA_TYPE_TO_FRIENDLY_NAME} from './parameterLibraryListingUtils';

export interface SchemaFieldSelectorOptionValue {
  id: string;
  name: string;
}

const useStyles = createStyles(() => ({
  icon: {
    marginRight: '4px',
  },
  selectedLabel: {
    display: 'flex',
    alignItems: 'center',
    maxWidth: 180,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));

const DATA_TYPE_TO_ICON: Record<LibraryParameterDefinitionDataType, BenchlingIconName> = {
  [FieldDefinitionType.FLOAT]: 'number',
  [FieldDefinitionType.TEXT]: 'text',
  [FieldDefinitionType.DATETIME]: 'calendar',
  [FieldDefinitionType.DROPDOWN]: 'circle-caret-down',
};

/** @package */
export interface LibraryParameterDefinitionDataTypeSelectorProps {
  selectedType: LibraryParameterDefinitionDataType;
  onChangeSelection: (parameterType: LibraryParameterDefinitionDataType) => void;
  onSelectDropdown?: (selector: SchemaFieldSelectorOptionValue | null) => void;
  selectedDropdownName?: string | null;
  dropdownOptions?: Array<SchemaFieldSelectorOptionValue>;
}

export function LibraryParameterDefinitionDataTypeSelector(
  props: LibraryParameterDefinitionDataTypeSelectorProps
): JSX.Element {
  const {classes} = useStyles();
  const selectorOptions = props.dropdownOptions ?? [];

  const placeholderText = 'Select data type';
  const selectedOptionLabel = props.selectedType ? (
    <div className={classes.selectedLabel}>
      <BenchlingIcon icon={DATA_TYPE_TO_ICON[props.selectedType]} className={classes.icon} />
      <span>
        {LIBRARY_PARAMETER_DEFINITION_DATA_TYPE_TO_FRIENDLY_NAME[props.selectedType]}
        {props.selectedType === FieldDefinitionType.DROPDOWN && props.selectedDropdownName
          ? `: ${props.selectedDropdownName}`
          : ''}
      </span>
    </div>
  ) : (
    placeholderText
  );

  const availableDataTypes = LIBRARY_PARAMETER_DEFINITION_DATA_TYPES.filter(
    (type) =>
      type !== FieldDefinitionType.DROPDOWN || BenchlingConfig.PROCEDURES_ENABLE_DROPDOWN_DATA_PARAMETER
  );

  return (
    <SelectDropdown
      displayName={selectedOptionLabel}
      buttonSize={ButtonSize.MINI}
      menu={
        <Menu value={props.selectedType}>
          {availableDataTypes.map((type: LibraryParameterDefinitionDataType, index: number) => {
            if (type === FieldDefinitionType.DROPDOWN && props.onSelectDropdown) {
              return (
                <DropdownDataTypeMenuOption
                  key={type}
                  menuOptionIndex={index}
                  selectorOptions={selectorOptions}
                  onChangeSelection={props.onChangeSelection}
                  onSelectDropdown={props.onSelectDropdown}
                />
              );
            }
            return (
              <Option
                icon={DATA_TYPE_TO_ICON[type]}
                key={type}
                value={type}
                onSelect={() => {
                  props.onChangeSelection(type);
                }}
                isSelected={props.selectedType === type}
              >
                {LIBRARY_PARAMETER_DEFINITION_DATA_TYPE_TO_FRIENDLY_NAME[type]}
              </Option>
            );
          })}
        </Menu>
      }
      dataTestAttrs={{component: 'LibraryParameterDefinitionDataTypeSelector', element: 'dropdown'}}
    />
  );
}

interface DropdownDataTypeMenuOptionProps {
  menuOptionIndex: number;
  selectorOptions: Array<SchemaFieldSelectorOptionValue>;
  onChangeSelection: (parameterType: LibraryParameterDefinitionDataType) => void;
  onSelectDropdown: (selector: SchemaFieldSelectorOptionValue | null) => void;
  // The following props are injected by `Menu` when this component is rendered as an option.
  // Do not define `_role` -- this is used internally by the menu.
  _role?: string;
  optionRef?: React.RefObject<HTMLElement>;
  isSelected?: boolean;
  className?: string;
  linkClassName?: string;
}

interface DropdownDataTypeMenuOptionState {
  optionRefs: {[ref: string]: HTMLLIElement | null};
}

/**
 * Renders the "Dropdown" data type as a menu option with a floating submenu listing the available
 * schema field selectors. Owns the option ref state used to anchor the floating submenu.
 *
 * Implemented as a class component so it can preserve `_role` on `defaultProps` (mirroring `Option`),
 * which `Menu` reads to treat this as a selectable option.
 */
class DropdownDataTypeMenuOption extends React.Component<
  DropdownDataTypeMenuOptionProps,
  DropdownDataTypeMenuOptionState
> {
  static defaultProps = {_role: ROLES.OPTION};

  override state: DropdownDataTypeMenuOptionState = {optionRefs: {}};

  override render(): JSX.Element {
    const {menuOptionIndex, selectorOptions, onChangeSelection, onSelectDropdown} = this.props;
    const optionRefName = getOptionRefForIndex(menuOptionIndex);

    return (
      <OptionContainer
        className={this.props.className}
        linkClassName={this.props.linkClassName}
        isSelected={this.props.isSelected}
        optionRef={this.props.optionRef}
        ref={(node) => {
          if (node != null && this.state.optionRefs[optionRefName] == null) {
            this.setState((prevState) => ({
              optionRefs: {...prevState.optionRefs, [optionRefName]: node.getRef()},
            }));
          }
        }}
      >
        <MenuOptionWithFloatingSubmenu
          menuOptionContainer={this.state.optionRefs[optionRefName]}
          menuOptionIndex={menuOptionIndex}
          optionData={{
            name: LIBRARY_PARAMETER_DEFINITION_DATA_TYPE_TO_FRIENDLY_NAME[FieldDefinitionType.DROPDOWN],
            icon: DATA_TYPE_TO_ICON[FieldDefinitionType.DROPDOWN],
            disabled: false,
            submenu: [[]],
            dataTestAttrs: {
              component: 'LibraryParameterDefinitionDataTypeSelector',
              element: 'dropdownOption',
            },
          }}
          submenuProps={{
            width: MenuWidth.LARGE,
            scrollable: true,
          }}
          renderOptionContentFn={defaultRenderOptionContent}
        >
          {selectorOptions.length === 0
            ? [
                <Option key="empty" isDisabled={true}>
                  No dropdowns found
                </Option>,
              ]
            : selectorOptions.map((selector) => (
                <Option
                  key={selector.id}
                  onSelect={() => {
                    onChangeSelection(FieldDefinitionType.DROPDOWN);
                    onSelectDropdown(selector);
                  }}
                >
                  {selector.name}
                </Option>
              ))}
        </MenuOptionWithFloatingSubmenu>
      </OptionContainer>
    );
  }
}
